import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import type {
  AdviserMilestoneRow,
  AdviserOption,
  ClassifiedGroup,
  ClassifiedPeople,
  ClassifiedPerson,
  CohortWindowFigures,
} from '../../../hooks/pipelineTracker/usePipelineReport';
import { averageDealTarget, type RagThresholds, type TargetsDoc } from '../../../hooks/pipelineTracker/targetsContract';
import { money, num } from './pipelineParts';
import { CENSORED_BELOW_DAYS } from './CohortBand';
import { TargetGradeLine } from './targetGradeLine';

/**
 * Who is carrying the book.
 *
 * WHY THIS IS NOT JUST A LIST OF DEAL OWNERS. Nothing in a Salestrekker export marks who is a
 * broker, so every distinct Deal Owner used to be rendered as a peer. Measured at Evoke, that put
 * four brokers carrying 2,142 deals in the same table as four support and admin users carrying 7
 * between them, which makes the table read as though the practice has eight brokers.
 *
 * NOTHING IS HIDDEN, THINGS ARE ARRANGED. Support is collapsed behind a disclosure that always
 * states its count, and the collapsed row still shows the deals. A person whose deals are inside
 * the company total but whose name appears nowhere on the page is exactly how a reader comes to
 * believe a figure has been filtered when it has not, and that is a worse failure than a long
 * list. The company total is unchanged by any of this: see roster/schema.ts on the server.
 *
 * SHARE IS OF THE SCOPED BOOK, NOT OF THE LISTED ROWS. The denominator is every in-flight deal in
 * scope, including support and grouped books, so the percentages add to 100 across everyone shown
 * rather than to 100 across the brokers alone. A share that silently renormalised itself when a
 * person was reclassified would move a number that nothing about the business had changed.
 *
 * THE SPREAD IS ATTAINMENT ON AN ENTRY COHORT, NOT A FUNNEL. Asked for by Evoke on 8 Sep 2026.
 * The five columns count what became of the deals each broker STARTED in the selected window; they
 * are not the same deals moving from one column to the next, so no ratio between two of them is a
 * conversion rate and none is shown. The columns add up to the cohort band above, which is the
 * check a reader should be able to make with their eyes, and since 25 Sep 2026 the table states
 * that check itself: a Total row first, and each cell's share of its column's total.
 *
 * DOLLARS OR DEALS, ONE TOGGLE. Each cell carries both, the chosen one large and the other under
 * it, so switching never hides a figure. The toggle appears only when the payload carries values;
 * an older server yields the count-only table exactly as before.
 *
 * AVERAGE DEAL AGAINST TARGET. When the tenant's targets document is passed in, the Avg settled
 * deal cell carries a line against the broker's OWN average deal target, and the Total row's
 * against the company's. A broker without one reads "No target set", never the company's figure
 * (targetsContract.ts averageDealTarget). The average is a level, so it is compared as it stands,
 * not pro-rated to the window. Without the document (not passed, or not loaded) the cell is exactly
 * what it was.
 */

/**
 * The column headings, in one place so the wording is a one-line change.
 *
 * "Meeting booked" rather than "Meetings": the export records that a meeting was arranged and
 * nothing about whether it happened, so the heading says the stronger of the two true things.
 */
export const SPREAD_COLUMNS = [
  { key: 'leads', valueKey: 'leadsValueCents', label: 'Leads' },
  { key: 'meetings', valueKey: 'meetingsValueCents', label: 'Meeting booked' },
  { key: 'lodged', valueKey: 'lodgedValueCents', label: 'Lodged' },
  { key: 'approved', valueKey: 'approvedValueCents', label: 'Approved' },
  { key: 'settled', valueKey: 'settledValueCents', label: 'Settled' },
] as const;

type SpreadMode = '$' | '#';

/** Every value field present on every row: the payload is new enough to offer dollars. */
const carriesValues = (rows: readonly AdviserMilestoneRow[]): boolean =>
  rows.length > 0 && rows.every((row) => SPREAD_COLUMNS.every((c) => typeof row[c.valueKey] === 'number'));

/**
 * A share of a column total, or null when no honest share exists. The export can carry a negative
 * deal value (counted in the report's data-quality exceptions), which can leave a column total at
 * or below zero or smaller than one row; a share outside 0 to 100 is then arithmetic, not meaning.
 */
const share = (part: number, whole: number): number | null =>
  whole <= 0 || part < 0 || part > whole ? null : Math.round((part / whole) * 100);

const HEAD_CELL = 'px-3 py-2 font-semibold';
const CELL = 'px-3 py-2.5';

/** Stated under the table, because five bare counts do not say what cohort they are of. */
export const SPREAD_NOTE =
  'Leads, Meeting booked, Lodged, Approved and Settled count what became of the deals each broker '
  + "created inside the selected window, read from each deal's milestone dates where the export "
  + 'carries them, and from the stage it sits in today where it does not. They are not the same '
  + 'deals moving from one column to the next, so the columns are not steps in a funnel. Meeting '
  + 'booked has no date either way and counts deals still sitting at that stage, because nothing '
  + 'in the export records a meeting that has already been passed.';

/** Added under the table when the payload carries values. */
export const SPREAD_VALUE_NOTE =
  'Dollar figures are the deal value recorded on exactly the deals each count counts; a deal with '
  + 'no recorded value counts once and adds nothing. "% of total" is a share of that column across '
  + 'everyone in scope, not a conversion rate. Avg settled deal is settled dollars over settled deals.';

/** Added under the table when the average deal is graded. */
export const AVERAGE_DEAL_TARGET_NOTE =
  "Avg settled deal is graded against each broker's own average deal target from the Targets editor, "
  + "and the Total row against the company's. The target is a per-deal figure, so it is not pro-rated to "
  + 'the window. A broker without one says so and is not graded against the company figure.';

/** The target for the average deal cell: the figure and the thresholds, present once targets loaded. */
interface AverageDealTarget {
  target: number | undefined;
  rag: RagThresholds;
}

export interface PeopleTableProps {
  people?: ClassifiedPeople;
  /** Fallback for a payload with no roster block: the flat list, exactly as before. */
  advisers: AdviserOption[];
  /** Every in-flight deal in scope. The share denominator. */
  inScope: number;
  /**
   * The cohort window the bands above are showing, so the spread describes the same deals.
   *
   * Absent from a payload built before the spread existed, and absent while a custom window is
   * selected but not yet applied. The table then renders the four columns it always rendered
   * rather than five empty ones.
   */
  cohort?: CohortWindowFigures;
  selectedAdviserId?: string;
  onSelectAdviser: (adviserId: string | undefined) => void;
  selectedGroupKey?: string;
  onSelectGroup: (groupKey: string | undefined) => void;
  /**
   * The Total row over the spread columns. On by default. The Pipeline tab turns it off (founder,
   * 5 Oct 2026): the cohort band above it already carries those totals, so the row repeated them.
   * Each cell's "% of total" still reads against the same column totals either way.
   */
  showTotalRow?: boolean;
  /**
   * The tenant's targets document, for the average deal line. Null while loading or when it could
   * not be loaded: the cell then says nothing about a target rather than "No target set".
   */
  targets?: TargetsDoc | null;
}

/**
 * Zero renders as a dot, matching the age-in-stage matrix: a column of noughts reads as noise.
 *
 * The dot is decoration and carries the real figure beside it for a screen reader, because a
 * middot announced on its own is a cell whose value a listener cannot obtain at all.
 */
const SpreadCells: React.FC<{
  spread?: AdviserMilestoneRow;
  /** Column totals across everyone in scope. Present once the table states shares. */
  total?: AdviserMilestoneRow;
  /** Present only when the payload carries values. */
  mode?: SpreadMode;
  /** Present once the targets document has loaded; undefined draws the cell exactly as before. */
  average?: AverageDealTarget;
}> = ({ spread, total, mode, average }) => (
  <>
    {SPREAD_COLUMNS.map((column) => {
      const count = spread ? spread[column.key] : 0;
      const cents = spread?.[column.valueKey] ?? 0;
      const dollars = mode === '$';
      const pct = total === undefined
        ? null
        : dollars
          ? share(cents, total[column.valueKey] ?? 0)
          : share(count, total[column.key]);
      // The average needs a positive settled total: see share() on negative deal values.
      return (
        <td key={column.key} className={`${CELL} text-right tabular-nums`}>
          {count === 0 ? (
            <>
              <span aria-hidden="true" className="text-gray-600">&middot;</span>
              <span className="sr-only">0</span>
            </>
          ) : (
            <>
              <span>{dollars ? money(cents) : num(count)}</span>
              {pct !== null && <span className="block text-[0.6875rem] text-gray-500">{pct}% of total</span>}
              {mode !== undefined && (
                <span className="block text-[0.6875rem] text-gray-500">
                  {dollars ? `${num(count)} ${count === 1 ? 'deal' : 'deals'}` : money(cents)}
                </span>
              )}
            </>
          )}
        </td>
      );
    })}
    {mode !== undefined && (() => {
      // The average needs settled deals with a positive settled total: see share() on negative values.
      const averageCents = spread && spread.settled > 0 && (spread.settledValueCents ?? 0) > 0
        ? Math.round((spread.settledValueCents ?? 0) / spread.settled)
        : null;
      return (
        <td className={`${CELL} text-right tabular-nums`}>
          {averageCents !== null ? money(averageCents) : (
            <>
              <span aria-hidden="true" className="text-gray-600">&middot;</span>
              <span className="sr-only">none settled</span>
            </>
          )}
          {average !== undefined && (
            <TargetGradeLine
              target={average.target}
              actual={averageCents}
              rag={average.rag}
              format={money}
              ungradedReason="none settled"
              className="justify-end"
            />
          )}
        </td>
      );
    })()}
  </>
);

/** Members summed into their group row, exactly as the group's other figures are. */
const sumSpread = (
  members: readonly ClassifiedPerson[],
  byKey: Map<string, AdviserMilestoneRow>
): AdviserMilestoneRow => {
  const rows: AdviserMilestoneRow[] = [];
  for (const member of members) {
    const row = byKey.get(member.adviserKey);
    if (row) rows.push(row);
  }
  return sumRows(rows);
};

/** Column totals. Values are summed only where every row carries them, so a partial sum never shows. */
const sumRows = (rows: readonly AdviserMilestoneRow[]): AdviserMilestoneRow => {
  const total: AdviserMilestoneRow = {
    adviserKey: '',
    adviserName: '',
    leads: 0,
    meetings: 0,
    lodged: 0,
    approved: 0,
    settled: 0,
  };
  const withValues = carriesValues(rows);
  for (const row of rows) {
    for (const column of SPREAD_COLUMNS) {
      total[column.key] += row[column.key];
      if (withValues) total[column.valueKey] = (total[column.valueKey] ?? 0) + (row[column.valueKey] ?? 0);
    }
  }
  return total;
};

const Share: React.FC<{ count: number; inScope: number }> = ({ count, inScope }) => {
  const share = inScope === 0 ? 0 : Math.round((count / inScope) * 100);
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-gray-900/70">
        <div className="h-full rounded-full bg-blue-500" style={{ width: `${share}%` }} />
      </div>
      <span className="w-9 text-right tabular-nums text-gray-400">{share}%</span>
    </div>
  );
};

const PersonRow: React.FC<{
  person: ClassifiedPerson | AdviserOption;
  inScope: number;
  selected: boolean;
  onSelect: () => void;
  indented?: boolean;
  muted?: boolean;
  spread?: AdviserMilestoneRow;
  showSpread?: boolean;
  total?: AdviserMilestoneRow;
  mode?: SpreadMode;
  average?: AverageDealTarget;
}> = ({ person, inScope, selected, onSelect, indented, muted, spread, showSpread, total, mode, average }) => (
  <tr
    onClick={onSelect}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect();
      }
    }}
    tabIndex={0}
    role="button"
    aria-label={`Scope this tab to ${person.adviserName}`}
    className={`cursor-pointer transition-colors hover:bg-gray-700/30 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 ${
      selected ? 'bg-blue-500/10 text-gray-100' : muted ? 'text-gray-400' : 'text-gray-300'
    }`}
  >
    <td className={`${CELL} ${indented ? 'pl-9' : ''}`}>{person.adviserName}</td>
    <td className={`${CELL} text-right tabular-nums`}>{num(person.inFlightCount)}</td>
    <td className={`${CELL} text-right tabular-nums text-gray-400`}>{num(person.totalCount)}</td>
    <td className={`${CELL} text-right`}>
      <Share count={person.inFlightCount} inScope={inScope} />
    </td>
    {showSpread && <SpreadCells spread={spread} total={total} mode={mode} average={average} />}
  </tr>
);

const GroupRows: React.FC<{
  group: ClassifiedGroup;
  inScope: number;
  selectedGroupKey?: string;
  selectedAdviserId?: string;
  onSelectGroup: (groupKey: string | undefined) => void;
  onSelectAdviser: (adviserId: string | undefined) => void;
  spreadByKey?: Map<string, AdviserMilestoneRow>;
  total?: AdviserMilestoneRow;
  mode?: SpreadMode;
  /** For the members. The book row itself has no target (a book is not a broker). */
  averageFor?: (adviserId: string | null) => AverageDealTarget | undefined;
}> = ({
  group,
  inScope,
  selectedGroupKey,
  selectedAdviserId,
  onSelectGroup,
  onSelectAdviser,
  spreadByKey,
  total,
  mode,
  averageFor,
}) => {
  const [open, setOpen] = useState(false);
  const selected = selectedGroupKey === group.key;

  return (
    <>
      <tr
        className={`transition-colors hover:bg-gray-700/30 ${
          selected ? 'bg-blue-500/10 text-gray-100' : 'text-gray-300'
        }`}
      >
        <td className={CELL}>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label={`${open ? 'Collapse' : 'Expand'} ${group.label}`}
              className="rounded p-0.5 text-gray-400 hover:bg-gray-700/50 hover:text-gray-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
            >
              {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
            <button
              type="button"
              onClick={() => onSelectGroup(selected ? undefined : group.key)}
              className="text-left hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
              aria-label={`Scope this tab to ${group.label}`}
            >
              {group.label}
            </button>
            <span className="text-[0.6875rem] text-gray-500">
              {num(group.members.length)} {group.members.length === 1 ? 'broker' : 'brokers'}
            </span>
          </div>
        </td>
        <td className={`${CELL} text-right tabular-nums`}>{num(group.inFlightCount)}</td>
        <td className={`${CELL} text-right tabular-nums text-gray-400`}>{num(group.totalCount)}</td>
        <td className={`${CELL} text-right`}>
          <Share count={group.inFlightCount} inScope={inScope} />
        </td>
        {spreadByKey && <SpreadCells spread={sumSpread(group.members, spreadByKey)} total={total} mode={mode} />}
      </tr>
      {open &&
        group.members.map((member) => (
          <PersonRow
            key={member.adviserKey}
            person={member}
            inScope={inScope}
            indented
            muted
            selected={selectedAdviserId === (member.adviserId ?? undefined)}
            onSelect={() => onSelectAdviser(member.adviserId ?? undefined)}
            showSpread={spreadByKey !== undefined}
            spread={spreadByKey?.get(member.adviserKey)}
            total={total}
            mode={mode}
            average={averageFor?.(member.adviserId)}
          />
        ))}
    </>
  );
};

const PeopleTable: React.FC<PeopleTableProps> = ({
  people,
  advisers,
  inScope,
  cohort,
  selectedAdviserId,
  onSelectAdviser,
  selectedGroupKey,
  onSelectGroup,
  showTotalRow = true,
  targets = null,
}) => {
  const [showSupport, setShowSupport] = useState(false);
  // Dollars first when the payload carries them, matching the movements row above.
  const [chosenMode, setChosenMode] = useState<SpreadMode>('$');

  // No roster block at all means an older payload. Render exactly what this table rendered before
  // the roster existed rather than an empty page.
  const rows = people ?? {
    brokers: [...advisers].sort((a, b) => b.inFlightCount - a.inFlightCount) as ClassifiedPerson[],
    groups: [],
    support: [],
    unclassifiedCount: 0,
    isSeed: true,
  };

  // Joined on the key the server folded on, so the spread and the broker chip cannot disagree
  // about who a person is. Undefined means the payload carries no spread, and the table then
  // renders the four columns it rendered before this feature existed.
  const spreadByKey = cohort?.advisers
    ? new Map(cohort.advisers.map((row) => [row.adviserKey, row]))
    : undefined;
  const showSpread = spreadByKey !== undefined;
  // Summed from the same rows the broker lines show, which the server folds in the same loop as
  // the cohort band, so the Total row equals the band by construction.
  const total = showSpread ? sumRows(cohort?.advisers ?? []) : undefined;
  const withValues = showSpread && carriesValues(cohort?.advisers ?? []);
  const mode: SpreadMode | undefined = withValues ? chosenMode : undefined;
  const listed: ReadonlyArray<{ inFlightCount: number; totalCount: number }> = [
    ...rows.brokers,
    ...rows.groups,
    ...rows.support,
  ];
  const listedTotals = listed.reduce(
    (acc, row) => ({ inFlight: acc.inFlight + row.inFlightCount, total: acc.total + row.totalCount }),
    { inFlight: 0, total: 0 },
  );
  // The same rule the cohort band applies to the same deals, reading the same windowDays from the
  // same payload. A young cohort has had days rather than months to convert, and these counts
  // read low for that reason rather than because a broker did less.
  const censored = showSpread && (cohort?.windowDays ?? 0) < CENSORED_BELOW_DAYS;
  const columnCount = showSpread ? 4 + SPREAD_COLUMNS.length + (withValues ? 1 : 0) : 4;
  // The average deal target per person, once the document has loaded and the table shows averages.
  // A person with no owner ref has no key the editor can set a target under, so they read "No target set".
  const averageFor = (adviserId: string | null): AverageDealTarget | undefined =>
    targets && withValues
      ? { target: adviserId === null ? undefined : averageDealTarget(targets, { adviserId }), rag: targets.rag }
      : undefined;
  const companyAverage: AverageDealTarget | undefined =
    targets && withValues ? { target: averageDealTarget(targets), rag: targets.rag } : undefined;

  return (
    <div className="overflow-x-auto">
      {withValues && (
        <div className="mb-2 flex justify-end">
          <div
            className="inline-flex rounded-lg border border-gray-700 bg-gray-900/60 p-0.5"
            role="group"
            aria-label="Show broker figures as"
          >
            {(['$', '#'] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={m === chosenMode}
                aria-label={m === '$' ? 'Dollars' : 'Deal counts'}
                onClick={() => setChosenMode(m)}
                className={`rounded-md px-2.5 py-1 text-[0.6875rem] font-bold transition-colors ${
                  m === chosenMode ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      )}
      <table className="w-full text-left text-sm">
        <thead className="text-[0.6875rem] uppercase tracking-wider text-gray-400">
          <tr>
            <th className={HEAD_CELL}>Broker</th>
            <th className={`${HEAD_CELL} text-right`}>In flight</th>
            <th className={`${HEAD_CELL} text-right`}>Total in export</th>
            <th className={`${HEAD_CELL} text-right`}>Share of book</th>
            {showSpread &&
              SPREAD_COLUMNS.map((column) => (
                <th key={column.key} scope="col" className={`${HEAD_CELL} text-right`}>
                  {column.label}
                </th>
              ))}
            {withValues && (
              <th scope="col" className={`${HEAD_CELL} text-right`}>Avg settled deal</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700/50">
          {/* The column totals, first, so every share below reads against a figure on screen. */}
          {showTotalRow && showSpread && total !== undefined && (
            <tr className="bg-white/[0.025] font-semibold text-gray-100">
              <td className={CELL}>
                Total
                <span className="ml-2 text-[0.6875rem] font-normal text-gray-500">everyone listed</span>
              </td>
              {/* Summed from the rows listed below, so the Total row can never disagree with them
                  under a broker, group, deal type or label filter. */}
              <td className={`${CELL} text-right tabular-nums`}>{num(listedTotals.inFlight)}</td>
              <td className={`${CELL} text-right tabular-nums text-gray-400`}>{num(listedTotals.total)}</td>
              <td className={CELL} />
              <SpreadCells spread={total} mode={mode} average={companyAverage} />
            </tr>
          )}
          {rows.brokers.map((person) => (
            <PersonRow
              key={person.adviserKey ?? person.adviserId ?? person.adviserName}
              person={person}
              inScope={inScope}
              selected={selectedAdviserId === (person.adviserId ?? undefined)}
              onSelect={() => onSelectAdviser(person.adviserId ?? undefined)}
              showSpread={showSpread}
              spread={spreadByKey?.get(person.adviserKey)}
              total={total}
              mode={mode}
              average={averageFor(person.adviserId)}
            />
          ))}

          {rows.groups.map((group) => (
            <GroupRows
              key={group.key}
              group={group}
              inScope={inScope}
              selectedGroupKey={selectedGroupKey}
              selectedAdviserId={selectedAdviserId}
              onSelectGroup={onSelectGroup}
              onSelectAdviser={onSelectAdviser}
              spreadByKey={spreadByKey}
              total={total}
              mode={mode}
              averageFor={averageFor}
            />
          ))}

          {rows.support.length > 0 && (
            <tr className="text-gray-400">
              <td colSpan={columnCount} className="px-3 py-2">
                <button
                  type="button"
                  onClick={() => setShowSupport((v) => !v)}
                  aria-expanded={showSupport}
                  className="flex items-center gap-1.5 text-[0.8125rem] text-gray-400 hover:text-gray-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
                >
                  {showSupport ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                  {/* The count is stated whether or not the rows are shown. These deals are in
                      every total on this page, and saying so is the difference between a tidy
                      table and one that looks filtered. */}
                  {num(rows.support.length)} support{' '}
                  {rows.support.length === 1 ? 'user' : 'users'} holding{' '}
                  {num(rows.support.reduce((sum, person) => sum + person.totalCount, 0))} deals,
                  counted in every figure above
                </button>
              </td>
            </tr>
          )}

          {showSupport &&
            rows.support.map((person) => (
              <PersonRow
                key={person.adviserKey ?? person.adviserName}
                person={person}
                inScope={inScope}
                indented
                muted
                selected={selectedAdviserId === (person.adviserId ?? undefined)}
                onSelect={() => onSelectAdviser(person.adviserId ?? undefined)}
                showSpread={showSpread}
                spread={spreadByKey?.get(person.adviserKey)}
                total={total}
                mode={mode}
                average={averageFor(person.adviserId)}
              />
            ))}
        </tbody>
      </table>

      {showSpread && (
        <>
          {censored && (
            <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-amber-300/90">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" />
              <span>
                This window covers {num(cohort?.windowDays ?? 0)} days. Deals created inside it have
                not had time to reach a lender or settle, so these columns read low by construction
                for every broker. Compare a full year before reading anything into them.
              </span>
            </p>
          )}
          <p className="mt-3 text-xs leading-relaxed text-gray-500">{SPREAD_NOTE}</p>
          {withValues && <p className="mt-2 text-xs leading-relaxed text-gray-500">{SPREAD_VALUE_NOTE}</p>}
          {companyAverage !== undefined && <p className="mt-2 text-xs leading-relaxed text-gray-500">{AVERAGE_DEAL_TARGET_NOTE}</p>}
        </>
      )}
    </div>
  );
};

export default PeopleTable;
